import ToggleInput from "@/components/toggle/Toggle";
import React, { useEffect, useState } from "react";
import ToggleSwitch from "./ToggleSwitch";

const fmt = (n: number) =>
    n.toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 2 });

type Props = {
    enabled?: boolean;
    onEnabledChange?: (v: boolean) => void;

    name?: string;
    amount?: string | number;
    percentage?: string | number;
    /** Max discount allowed (e.g. subtotal minus platform fee). Discount is clamped to this on Apply. */
    maxDiscount?: number;
    /** Subtotal (package total) used to compute discount value when percentage is entered. Required to show error for percentage over max. */
    subTotal?: number;

    onChange?: (next: {
        name: string;
        amount: string;
        percentage: string;
    }) => void;

    onApply?: (data: {
        name: string;
        amount: number;
        percentage: number;
    }) => void;

    className?: string;
};

export default function DiscountCard({
    enabled: controlledEnabled,
    onEnabledChange,

    name: cName = "",
    amount: cAmount = "",
    percentage: cPct = "",
    maxDiscount,
    subTotal = 0,

    onChange,
    onApply,
    className = "",
}: Props) {
    // -------
    // ---------------------
    // ✅ ENABLE STATE (Controlled + Uncontrolled)
    // ----------------------------
    const [internalEnabled, setInternalEnabled] = useState(false);


    // If parent provides enabled → use controlled
    const enabled =
        controlledEnabled !== undefined ? controlledEnabled : internalEnabled;

    // Sync internal state when backend updates enabled prop
    useEffect(() => {
        if (controlledEnabled !== undefined) {
            setInternalEnabled(controlledEnabled);
        }
    }, [controlledEnabled]);

    // ----------------------------
    // ✅ FORM STATE
    // ----------------------------
    const [name, setName] = useState(String(cName) || "Discount");
    const [amount, setAmount] = useState(String(cAmount));
    const [percentage, setPercentage] = useState(String(cPct));
    const [error, setError] = useState<string | null>(null);

    // Sync input values from backend; amount always formatted to 2 decimal places when numeric
    useEffect(() => setName(String(cName) || "Discount"), [cName]);
    useEffect(() => {
        const v = cAmount;
        if (v === "" || v === null || v === undefined) {
            setAmount("");
            return;
        }
        const num = Number(v);
        if (!Number.isNaN(num)) {
            setAmount(Number(num).toFixed(2));
        } else {
            setAmount(String(v));
        }
    }, [cAmount]);
    useEffect(() => {
        const v = cPct;
        if (v === "" || v === null || v === undefined) {
            setPercentage("");
            return;
        }
        const num = Number(v);
        if (!Number.isNaN(num)) {
            setPercentage(Number(num).toFixed(2));
        } else {
            setPercentage(String(v));
        }
    }, [cPct]);

    // ----------------------------
    // ✅ TOGGLE HANDLER
    // ----------------------------
    const toggle = () => {
        const next = !enabled;

        // Controlled mode
        if (onEnabledChange) {
            onEnabledChange(next);
        }
        // Uncontrolled fallback
        else {
            setInternalEnabled(next);
        }
    };

    // ----------------------------
    // ✅ UPDATE INPUT HANDLER
    // ----------------------------
    const update = (
        next: Partial<{ name: string; amount: string; percentage: string }>
    ) => {
        const draft = {
            name: next.name ?? name,
            amount: next.amount ?? amount,
            percentage: next.percentage ?? percentage,
        };

        setName(draft.name);
        setAmount(draft.amount);
        setPercentage(draft.percentage);

        onChange?.(draft);
    };

    // ----------------------------
    // ✅ APPLY HANDLER
    // ----------------------------
    const handleApply = () => {
        onApply?.({
            name: name.trim() ? name : "Discount",
            amount: Number(amount || 0),
            percentage: Number(percentage || 0),
        });
    };

    // Both fields always editable (no mutual disable)
    const amountDisabled = false;
    const percentageDisabled = false;

    // ----------------------------
    // ✅ OVER-MAX VALIDATION (show error when discount > maxDiscount)
    // ----------------------------
    const discountValue = amount && Number(amount) > 0
        ? Number(amount)
        : percentage && Number(percentage) > 0 && subTotal > 0
            ? (subTotal * Number(percentage)) / 100
            : 0;
    const overMaxError = enabled && maxDiscount != null && maxDiscount > 0 && discountValue > maxDiscount;

    // ----------------------------
    // ✅ APPLIED STATE (based on last applied values from parent, not unsaved input)
    // ----------------------------
    const isApplied = enabled && (Number(cAmount) > 0 || Number(cPct) > 0);

    // ----------------------------
    // ✅ UI
    // ----------------------------
    return (
        <div
            className={["w-full rounded-2xl bg-[#F7F8FA]", "p-2 sm:p-5"].join(" ")}>
            {/* Header */}
            <div
                className={`flex items-center justify-between ${enabled ? "border-b-2 pb-3 -mx-2" : "-mx-2"
                    }`}
            >
                <div className="flex flex-1 items-center justify-between px-2">
                    <div className="flex items-center gap-2">
                        <p className="text-xs text-[#19191C] font-[500]">Discount</p>
                        {isApplied && (
                            <span className="text-[10px] font-medium text-primary-red bg-primary-red/10 px-2 py-0.5 rounded-full">
                                Applied
                            </span>
                        )}
                    </div>
                    <ToggleSwitch checked={enabled} onChange={() => toggle()} />
                </div>
            </div>

            {/* Body Expand */}
            <div
                className={[
                    "grid transition-[grid-template-rows] duration-300 ease-out",
                    enabled ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                ].join(" ")}
            >
                <div className="min-h-0 overflow-hidden">
                    <div className="pt-3 px-1">
                        {/* Discount Name */}
                        <label className="block text-xs font-semibold text-gray-700 mb-1 pl-2">
                            Discount Name
                        </label>

                        <input
                            type="text"
                            placeholder="Name"
                            value={name}
                            onChange={(e) => update({ name: e.target.value })}
                            className="w-full mt-1 h-9 rounded-full bg-white border border-gray-200 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-primary-red"
                        />

                        {/* {maxDiscount != null && maxDiscount > 0 && (
                            <p className="text-xs text-gray-500 mb-2 pl-2">
                                Max discount: {fmt(maxDiscount)}
                            </p>
                        )} */}
                        {/* Amount + Percentage */}
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {/* Amount */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1 pl-2">
                                    Amount
                                </label>

                                <input
                                    type="number"
                                    placeholder="Amount"
                                    value={amount}
                                    step="0.01"
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        const parts = raw.split(".");
                                        const allowed =
                                            parts.length === 2
                                                ? `${parts[0]}.${parts[1].slice(0, 2)}`
                                                : raw;
                                        update({ amount: allowed, percentage: "" });
                                    }}
                                    disabled={amountDisabled}
                                    className="w-full mt-1 h-9 rounded-full bg-white border border-gray-200 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-primary-red"
                                />
                            </div>

                            {/* Percentage */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1 pl-2">
                                    Percentage
                                </label>

                                <input
                                    type="number"
                                    placeholder="Percentage"
                                    value={percentage}
                                    min={0}
                                    max={100}
                                    step="0.01"
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        const parts = raw.split(".");
                                        const allowed =
                                            parts.length === 2
                                                ? `${parts[0]}.${parts[1].slice(0, 2)}`
                                                : raw;
                                        const num = Number(allowed);

                                        let valueToUse = allowed;
                                        if (num > 100) {
                                            setError("Percentage must be between 0 and 100");
                                            valueToUse = "100";
                                        } else if (num < 0) {
                                            setError("Percentage must be between 0 and 100");
                                            valueToUse = "0";
                                        } else {
                                            setError(null);
                                        }
                                        update({ percentage: valueToUse, amount: "" });
                                    }}
                                    disabled={percentageDisabled}
                                    className="w-full mt-1 h-9 rounded-full bg-white border border-gray-200 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-primary-red"
                                />

                                {error && (
                                    <p className="mt-1 text-xs text-red-500">{error}</p>
                                )}
                            </div>
                        </div>

                        {overMaxError && (
                            <p className="mt-2 text-xs text-primary-red pl-2">
                                Discount cannot exceed {fmt(maxDiscount)}. Please enter a lower value.
                            </p>
                        )}

                        {/* Apply */}
                        <button
                            type="button"
                            onClick={handleApply}
                            className="mt-3 w-full h-9 rounded-full bg-primary-red text-white text-sm font-medium hover:bg-orange-600 transition"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
